import React, { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EnhancedSidebar } from '@/components/common/EnhancedSidebar';
import { useIsAuthenticated } from '@/hooks/redux';
import { cn, formatNumberWithCommas, showToastMessage } from '@/utils';
import { RootState } from '@/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  useAvailbleRolesQuery,
  useGetUserQuery,
  useGetWalletQuery,
  useLogoutMutation,
} from '@/store/api/authApi';
import { clearUser } from '@/store/slices/userSlice';
import { logout as clearAuth } from '@/store';
import apiSlice from '@/store/api/apiSlice';
import { STATUS_LOGOUT_MESSAGES } from '@/utils/constent';
export function DashboardLayout() {
  const [logout] = useLogoutMutation();
  const BLOCKED_STATUSES = ['inactive', 'suspended'];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ismobile = window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = React.useState(!ismobile);
  const isAuthenticated = useIsAuthenticated();
  const { data: userData } = useGetUserQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 20000,
  });

  useAvailbleRolesQuery(undefined, { skip: !isAuthenticated });
  const { data } = useGetWalletQuery(undefined, { skip: !isAuthenticated, pollingInterval: 20000 });
  const user = useSelector((state: RootState) => state.user);
  const mainAmount = useMemo(() => {
    return data?.find((w) => w.type === 'Main')?.amount ?? '0';
  }, [data]);
  useEffect(() => {
    if (!userData?.status) return;

    if (BLOCKED_STATUSES.includes(userData.status.toLowerCase())) {
      // 🔹 Clear auth (example)
      const message =
        STATUS_LOGOUT_MESSAGES[userData?.status.toLowerCase()] ??
        'Your session has ended. Please log in again.';

      handleLogout(message);
      navigate('/login', { replace: true });
    }
  }, [userData?.status, navigate]);
  const handleLogout = async (message: string) => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      apiSlice.util.resetApiState();

      showToastMessage('error', message);
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background ">
      {/* Enhanced Sidebar */}
      <EnhancedSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      {/* Main Content */}
      <div className={cn('transition-all duration-300', sidebarOpen ? 'lg:ml-72' : 'lg:ml-16')}>
        {/* Top Navigation */}
        <header className="sticky top-0 z-40 border-b border-green3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center px-4 bg-white">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className=""
              // className="md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="border border-green3 rounded-lg flex-end ml-auto gap-2  px-4 ">
              <img src="/images/profile.png" alt="" className="h-10 w-10" />
              <div className="flex flex-col justify-start items-start py-1 font-semibold">
                <span className="leading-4">{user?.data?.username || 'Owner'}</span>
                <span className="flex items-center">{formatNumberWithCommas(mainAmount)}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 py-2 sm:py-6 bg-main-bg min-h-[calc(100dvh-65px)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
